import type { components } from '@/api/schema';

export type AdminRole = components['schemas']['AdminSessionDto']['role'];

export interface AdminSession {
  accessToken: string;
  adminId: string;
  role: AdminRole;
  /** Epoch ms at which the token stops being accepted. */
  expiresAt: number;
}

/**
 * Where the admin session lives.
 *
 * ⚠️ **`sessionStorage`, never `localStorage`.** An admin token reaches every
 * family's data in the product. `sessionStorage` is scoped to the tab and dies
 * with it, so closing the browser ends the session; `localStorage` would leave
 * it on a shared machine indefinitely. The in-memory copy is the one actually
 * read on the request path — storage is only there so a refresh does not sign
 * the admin out mid-task.
 *
 * The idle timeout is 30 minutes (06 §3). It is enforced here so the UI can log
 * out cleanly, and again by the server, which is the control that matters — a
 * client-side timer is a courtesy, not a boundary.
 */
const STORAGE_KEY = 'bs-admin-session';
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

type Listener = (session: AdminSession | null) => void;

let current: AdminSession | null = null;
const listeners = new Set<Listener>();
let hydrated = false;

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    // Private browsing or a hardened profile can throw on access. An admin who
    // cannot persist simply signs in again after a refresh.
    return null;
  }
}

function isValid(value: unknown): value is AdminSession {
  if (!value || typeof value !== 'object') return false;
  const s = value as Partial<AdminSession>;
  return (
    typeof s.accessToken === 'string' &&
    typeof s.adminId === 'string' &&
    typeof s.role === 'string' &&
    typeof s.expiresAt === 'number'
  );
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;

  const raw = storage()?.getItem(STORAGE_KEY);
  if (!raw) return;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (isValid(parsed) && parsed.expiresAt > Date.now()) current = parsed;
    else storage()?.removeItem(STORAGE_KEY);
  } catch {
    storage()?.removeItem(STORAGE_KEY);
  }
}

function emit(): void {
  for (const listener of listeners) listener(current);
}

/**
 * The session as it stands, without side effects.
 *
 * ⚠️ This is what `useSession` reads, so it must not mutate or notify: clearing
 * an expired session from here would emit to subscribers in the middle of a
 * render, which React rejects. Expiry is acted on by `getSession` — called from
 * event handlers and the request path — and by the idle timer in the layout.
 */
export function readSession(): AdminSession | null {
  hydrate();
  if (current && current.expiresAt <= Date.now()) return null;
  return current;
}

export function getSession(): AdminSession | null {
  hydrate();
  if (current && current.expiresAt <= Date.now()) clearSession();
  return current;
}

export function getAccessToken(): string | null {
  return getSession()?.accessToken ?? null;
}

export function setSession(
  session: Omit<AdminSession, 'expiresAt'> & { expiresIn: number },
): AdminSession {
  hydrate();
  const next: AdminSession = {
    accessToken: session.accessToken,
    adminId: session.adminId,
    role: session.role,
    expiresAt: Date.now() + session.expiresIn * 1000,
  };
  current = next;
  storage()?.setItem(STORAGE_KEY, JSON.stringify(next));
  emit();
  return next;
}

export function clearSession(): void {
  hydrated = true;
  current = null;
  storage()?.removeItem(STORAGE_KEY);
  emit();
}

/**
 * Swaps in a token the server renewed mid-session.
 *
 * The admin API returns `x-admin-token` once a token is past halfway through
 * its life, which is what makes the 30-minute timeout an IDLE one rather than a
 * hard cap on how long an admin may work. Keeping the token and the expiry in
 * step is the whole point: `touchSession` alone moved the expiry while the
 * token underneath it stayed on its original 30-minute clock, so the panel
 * believed it was signed in and the next call 401'd.
 *
 * Identity is never taken from the header — only the token. `adminId` and
 * `role` stay as they were established at sign-in, so a renewal cannot change
 * who the panel thinks it is. A role change revokes the session server-side and
 * the next request fails, which is the intended path.
 */
export function renewSession(accessToken: string, expiresIn: number): void {
  hydrate();
  if (!current) return;

  current = { ...current, accessToken, expiresAt: Date.now() + expiresIn * 1000 };
  storage()?.setItem(STORAGE_KEY, JSON.stringify(current));
  // No `emit()`. Renewal changes nothing a component renders, and notifying
  // subscribers on every other API call would re-render the whole panel.
}

/**
 * Pushes the expiry out on activity, for the window before the server has
 * renewed the token. `renewSession` is what actually extends the session; this
 * keeps the client from signing an active admin out a moment early.
 */
export function touchSession(): void {
  const session = getSession();
  if (!session) return;
  current = { ...session, expiresAt: Date.now() + IDLE_TIMEOUT_MS };
  storage()?.setItem(STORAGE_KEY, JSON.stringify(current));
}

export function subscribeToSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test seam. Production code changes the session through the functions above. */
export function resetSessionForTests(): void {
  current = null;
  hydrated = false;
  listeners.clear();
  storage()?.removeItem(STORAGE_KEY);
}
